import {
    Button,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Text,
    VStack,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import PartBayButton from '../PartBayButton';
import { LocationsPicker } from './UsersInlineCells';
import type { LocationEntry } from '../../hooks/useGetLocations';

export interface OemBulkCertificationsModalProps {
    isOpen: boolean;
    selectedCount: number;
    locations: LocationEntry[];
    /** The picked locations, applied to every selected make. */
    onApply: (value: Array<string> | 'all') => void;
    onClose: () => void;
}

/**
 * One set of certification locations, applied to every selected make. Like the
 * inline cell, the change is staged — the section's save bar commits it.
 */
const OemBulkCertificationsModal = ({
    isOpen,
    selectedCount,
    locations,
    onApply,
    onClose,
}: OemBulkCertificationsModalProps) => {
    const [value, setValue] = useState<Array<string> | 'all'>('all');

    // Reset between openings so a previous pick never carries over.
    useEffect(() => {
        if (isOpen) setValue('all');
    }, [isOpen]);

    return (
        <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader fontSize="md" fontWeight="semibold">
                    Set certifications
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody pb={2}>
                    <VStack align="stretch" spacing={4}>
                        <Text fontSize="sm" color="gray.500">
                            Applies to {selectedCount} selected make
                            {selectedCount === 1 ? '' : 's'}.
                        </Text>
                        <LocationsPicker
                            value={value}
                            locations={locations}
                            onChange={setValue}
                            maxH="260px"
                        />
                    </VStack>
                </ModalBody>
                <ModalFooter gap={2}>
                    <Button size="sm" variant="outlineGray" onClick={onClose}>
                        Cancel
                    </Button>
                    <PartBayButton
                        size="sm"
                        variant="solid"
                        colorScheme="partbay-blue"
                        onClick={() => onApply(value)}
                        text="Apply"
                    />
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default OemBulkCertificationsModal;
